import { useQuery } from "@tanstack/react-query";
import { Loader, Upload } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { ActivityCards } from "../components/ActivityCards";
import { ConnectSpotify } from "../components/ConnectSpotify";
import { EmptyState } from "../components/EmptyState";
import { HomeEmptyState } from "../components/HomeEmptyState";
import { QuickStats } from "../components/QuickStats";
import { RecentlyPlayedGrid } from "../components/RecentlyPlayedGrid";
import { WelcomeCard } from "../components/WelcomeCard";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalFilesStore } from "../stores/localFilesStore";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlayerStore } from "../stores/playerStore";
import { useRecentlyPlayedStore } from "../stores/recentlyPlayedStore";
import { useStatsStore } from "../stores/statsStore";
import { Track } from "../types";

export const Home = () => {
  const { isAuthenticated, login } = useSpotifyAuth();
  const { getAllAlbums, initialized: libraryInitialized } = useLocalLibraryStore();
  const { files, addFile, cleanup, loadFilesFromDB, initialized: filesInitialized } = useLocalFilesStore();
  const { getRecentTracks } = useRecentlyPlayedStore();
  const { setCurrentTrack, setIsPlaying } = usePlayerStore();
  const stats = useStatsStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: recentTracks, isLoading: isLoadingSpotifyTracks } = useQuery({
    queryKey: ["recent-tracks"],
    queryFn: () => spotifyApi.player.getRecentlyPlayedTracks(),
    enabled: isAuthenticated,
  });

  useEffect(() => {
    const initializeFiles = async () => {
      console.log("Home component initialization check:", { filesInitialized, libraryInitialized });

      // If library is initialized but files aren't, try to load files
      if (libraryInitialized && !filesInitialized) {
        setIsLoading(true);
        try {
          console.log("Loading files from DB...");
          await loadFilesFromDB();
        } catch (err) {
          console.error("Error loading files:", err);
          setError("Failed to load local files");
        } finally {
          setIsLoading(false);
        }
      } else if (libraryInitialized && filesInitialized) {
        // Both are initialized, stop loading
        console.log("Both stores initialized, stopping loading");
        setIsLoading(false);
      } else if (!libraryInitialized) {
        // Library isn't initialized yet, keep loading
        console.log("Waiting for library to initialize...");
        setIsLoading(true);
      }
    };

    initializeFiles();
  }, [filesInitialized, libraryInitialized, loadFilesFromDB]);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) return;

      setIsLoading(true);
      setError(null);

      try {
        const filePromises = Array.from(e.target.files).map((file) => addFile(file));
        await Promise.all(filePromises);
      } catch (err) {
        console.error("Error processing files:", err);
        setError("Failed to process audio files. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [addFile]
  );

  const DEMO_TRACK_IDS = ["demo-1", "demo-2", "demo-3"];
  const DEMO_ALBUM_ID = "demo-album"; // Update this to match tracks.json

  const localAlbums = getAllAlbums();
  const localRecent = getRecentTracks();
  const listenerType = stats.getListenerType();
  const uniqueTracks = stats.getUniqueTracksPlayed();
  const uniqueArtists = stats.getUniqueArtistsPlayed();

  // Check if only demo content exists
  const hasOnlyDemoContent =
    files.every((track) => DEMO_TRACK_IDS.includes(track.id)) &&
    localAlbums.every((album) => album.id === DEMO_ALBUM_ID);

  // Check if there's any non-demo content
  const hasAnyLocalFiles = files.some((track) => !DEMO_TRACK_IDS.includes(track.id));
  const hasAnyUserAlbums = localAlbums.some((album) => album.id !== DEMO_ALBUM_ID);
  const hasNoContent = (!hasAnyLocalFiles && !hasAnyUserAlbums && !isAuthenticated) || hasOnlyDemoContent;

  if (isLoading || isLoadingSpotifyTracks) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader className="w-8 h-8 animate-spin text-yellow-500" />
        <p className="text-zinc-400 mt-4">Loading your music...</p>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={Upload}
        title="Error loading your music"
        description={error}
        action={{
          label: "Try Again",
          onClick: () => loadFilesFromDB(),
        }}
      />
    );
  }

  // Show empty state if there's only demo content or no content at all
  if (hasNoContent) {
    return <HomeEmptyState />;
  }

  const handlePlay = (track: Track) => {
    setCurrentTrack(track);
    setIsPlaying(true);
  };

  const topArtist = stats.getTopArtist();
  const topTrack = stats.getTopTrack();

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-black">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8 space-y-6">
        {/* Welcome Card */}
        <WelcomeCard
          playlists={localAlbums.length}
          albums={localAlbums.length}
          tracks={uniqueTracks + files.length}
          artists={uniqueArtists}
          listenerType={listenerType.type}
        />

        {/* Activity Cards */}
        <ActivityCards
          likedSongs={stats.getLikedSongsCount()}
          recentlyPlayed={localRecent.length}
          topArtist={topArtist?.name || ""}
          topSong={topTrack?.title || ""}
          topSongUrl={topTrack?.id ? "/tracks/" + topTrack?.id : "/library"}
        />

        {/* Recently Played */}
        {(localRecent.length > 0 || recentTracks?.items.length) && (
          <RecentlyPlayedGrid
            tracks={[
              ...localRecent,
              ...(recentTracks?.items.map((item) => ({
                id: item.track.id,
                title: item.track.name,
                artist: item.track.artists[0].name,
                albumArt: item.track.album.images[0]?.url,
                duration: item.track.duration_ms / 1000,
                url: item.track.preview_url || "",
                source: "spotify" as const,
                spotifyData: item.track,
              })) || []),
            ]}
            onPlay={handlePlay}
            hideLabels
          />
        )}

        {/* Spotify Connect Prompt */}
        {!isAuthenticated && <ConnectSpotify onConnect={login} />}

        {/* Quick Stats */}
        <QuickStats
          currentStreak={stats.getCurrentStreak()}
          todaysListening={stats.getPlayTimeByPeriod("day") / 60}
          discoveryRate={stats.getListenerMetrics().uniqueArtistsRatio}
          averageDaily={stats.getAverageSessionDuration() / 60}
        />
      </div>
    </div>
  );
};
